import React, { forwardRef, Ref } from 'react';
import Link, { LinkProps as NextLinkProps } from 'next/link';
import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mui/material';
import { usePageLoader } from '@/hooks/usePageLoader';
import { usePathname, useSearchParams } from 'next/navigation';

type LinkRef = HTMLAnchorElement;

const NextLink = (
  {
    href,
    as,
    prefetch,
    underline = 'always',
    ...props
  }: NextLinkProps<string> & MuiLinkProps,
  ref: Ref<LinkRef>,
) => {
  const { setLoading } = usePageLoader();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [lastHistoryIndex, setLastHistoryIndex] = React.useState(0);

  const onClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    const element = e.target as HTMLElement;
    const link = element.closest('a');
    if (!link) return;

    const href = link.href;

    if (href && href === window.location.href) {
      return;
    }

    setLoading(true);
  };

  const handlePopstate = () => {
    const currentIndex = window.history.length;

    if (lastHistoryIndex < currentIndex) {
      // ブラウザバック時
      return;
    }

    setLoading(true);
  };

  React.useEffect(() => {
    window.addEventListener('popstate', handlePopstate, false);

    return () => {
      window.removeEventListener('popstate', handlePopstate, false);
      setLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  React.useEffect(() => {
    setLastHistoryIndex(window.history.length);

    return () => {
      setLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  return (
    <Link
      href={href}
      as={as}
      prefetch={prefetch}
      passHref
      style={{
        textDecoration: 'none',
        color: (props.color as string) || undefined,
      }}
      onClick={onClick}
    >
      <MuiLink ref={ref} component="span" underline={underline} {...props} />
    </Link>
  );
};

export default forwardRef(NextLink);
